import {
  connect as wagmiConnect,
  disconnect as wagmiDisconnect,
  switchNetwork,
} from '@wagmi/core';
import type { PropsWithChildren } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UserRejectedRequestError } from 'viem';
import type { Address, Connector } from 'wagmi';
import { useAccount } from 'wagmi';

import { useNetwork } from '../../../hooks';
import {
  CHAIN_ID_TO_KEY_MAP,
  CHAIN_ID_TO_TYPE_MAP,
  WEB3_CONFIG,
} from '../../../lib';
import { saveChainToSs } from '../../../utils';
import { Web3ChainStatusContext } from '../web3-chain-status';
import { Web3StateContext } from './context';
import type { WalletDetails } from './types';
import { chainToWalletChain, getInitialChain, getWalletBalance } from './utils';

const ZERO_BALANCE = {
  decimals: 18,
  formatted: '0',
  raw: 0n,
};

const initialChain = getInitialChain();

export const INITIAL_WALLET_DETAILS: WalletDetails = {
  address: null,
  chain: {
    id: initialChain.id,
    key: CHAIN_ID_TO_KEY_MAP[initialChain.id],
    type: CHAIN_ID_TO_TYPE_MAP[initialChain.id],
    explorerUrl: initialChain.blockExplorers.default.url,
    isSupported: true,
  },
  balance: {
    native: ZERO_BALANCE,
    usdt: ZERO_BALANCE,
    usdc: ZERO_BALANCE,
    stars: ZERO_BALANCE,
  },
};

export const Web3StateProvider = ({ children }: PropsWithChildren<{}>) => {
  const [walletDetails, setWalletDetails] = useState(INITIAL_WALLET_DETAILS);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isSwitching } = useContext(Web3ChainStatusContext);
  const { chain } = useNetwork();
  const { address, isConnected, isReconnecting, isConnecting, isDisconnected } =
    useAccount();

  const isLoadingOrReconnecting = useMemo(
    () => isLoading || isReconnecting || isConnecting || isSwitching,
    [isLoading, isReconnecting, isConnecting, isSwitching],
  );

  const resetStatus = useCallback(() => setIsInitialized(false), []);

  const updateChain = useCallback(
    async (_chain: ReturnType<typeof useNetwork>['chain']) => {
      const walletChain = chainToWalletChain(_chain);

      saveChainToSs(_chain?.id ?? WEB3_CONFIG.chains.bsc.id);

      if (!address || !_chain || _chain.unsupported) {
        setWalletDetails(p => ({
          ...p,
          balance: INITIAL_WALLET_DETAILS.balance,
          chain: walletChain,
        }));

        return;
      }

      setIsLoading(true);

      const balance = await getWalletBalance(address, _chain.id);

      setWalletDetails({ address, chain: walletChain, balance });
      setIsLoading(false);
    },
    [address],
  );

  const disconnect = useCallback(async () => {
    try {
      setIsLoading(true);

      await wagmiDisconnect();

      setWalletDetails(p => {
        const targetChain = p.chain.isSupported ? p.chain : getInitialChain();

        saveChainToSs(targetChain.id);

        return {
          address: null,
          chain: targetChain,
          balance: INITIAL_WALLET_DETAILS.balance,
        };
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const retrieveConnect = useCallback(
    async (
      _address: Address,
      _chain: NonNullable<ReturnType<typeof useNetwork>['chain']>,
    ) => {
      if (_chain?.unsupported) {
        saveChainToSs(_chain.id);
        setWalletDetails({
          address: _address,
          balance: INITIAL_WALLET_DETAILS.balance,
          chain: chainToWalletChain(_chain),
        });

        return;
      }

      try {
        const walletBalances = await getWalletBalance(
          _address,
          _chain.id as Parameters<typeof getWalletBalance>[1],
        );

        saveChainToSs(_chain.id);
        setWalletDetails({
          address: _address,
          balance: walletBalances,
          chain: chainToWalletChain(_chain),
        });
      } catch (error) {
        console.error(error);
        await disconnect();
      } finally {
        setIsInitialized(true);
      }
    },
    [],
  );

  const connect = useCallback(async (connector: Connector) => {
    try {
      setIsLoading(true);

      const cachedChainId = sessionStorage.getItem(
        process.env.REACT_APP_SS_CHAIN_ID_KEY!,
      );

      const targetChainId = Number(cachedChainId) || WEB3_CONFIG.chains.bsc.id;
      const targetChainKey = CHAIN_ID_TO_KEY_MAP[targetChainId];
      const targetChain = WEB3_CONFIG.chains[targetChainKey];

      const { account, chain: connectedChain } = await wagmiConnect({
        connector,
        chainId: targetChainId,
      });

      if (connectedChain.id !== targetChainId) {
        try {
          await switchNetwork({ chainId: targetChainId });
        } catch (error) {
          console.error(error);

          await wagmiDisconnect();

          throw new UserRejectedRequestError(
            new Error('user rejected request'),
          );
        }
      }

      return account;
    } catch (error) {
      console.error(error);

      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /** handles background connection */
  useEffect(() => {
    const targetCase = isConnected && !walletDetails.address;

    if (!targetCase || isLoadingOrReconnecting) {
      return;
    }

    void retrieveConnect(address!, chain!);
  }, [
    isLoadingOrReconnecting,
    walletDetails.address,
    isConnected,
    retrieveConnect,
  ]);

  /** handles background disconnection */
  useEffect(() => {
    const targetCase = isDisconnected && walletDetails.address;

    if (!targetCase || isLoadingOrReconnecting) {
      return;
    }

    setWalletDetails(p => ({
      ...p,
      address: null,
      balance: INITIAL_WALLET_DETAILS.balance,
    }));
  }, [
    isDisconnected,
    isLoadingOrReconnecting,
    walletDetails.address,
    disconnect,
  ]);

  /** handles background chain update */
  useEffect(() => {
    const targetCase = chain ? walletDetails.chain.id !== chain.id : false;

    if (!targetCase || isLoadingOrReconnecting) {
      return;
    }

    void updateChain(chain);
  }, [walletDetails.chain.id, chain, updateChain]);

  const contextState = useMemo(
    () => ({
      isInitialized,
      isLoading: isLoadingOrReconnecting,
      walletDetails,
      resetStatus,
      disconnect,
      connect,
      updateChain,
      updateWalletDetails: setWalletDetails,
    }),
    [
      isInitialized,
      walletDetails,
      isLoadingOrReconnecting,
      resetStatus,
      updateChain,
      disconnect,
      connect,
    ],
  );

  return (
    <Web3StateContext.Provider value={contextState}>
      {children}
    </Web3StateContext.Provider>
  );
};
